import { api } from './api'
import { getAll } from './pagination'

export type FacilityType = 'classroom' | 'laboratory' | 'library' | 'hall' | 'office' | 'sports' | 'cafeteria' | 'hostel' | 'playground' | 'garden' | 'parking' | 'other'
export type Condition = 'excellent' | 'good' | 'fair' | 'poor' | 'unsafe'

export interface Facility {
  id: number; name: string; description: string | null; facility_type: FacilityType
  capacity: number | null; area_sqm: string | null; building: string | null; floor: string | null; room_number: string | null
  equipment: string | null; notes: string | null; is_available: boolean; requires_booking: boolean; is_active: boolean
  open_requests: number; upcoming_bookings: number; last_inspection_date: string | null; last_condition: Condition | ''; next_maintenance_date: string | null
  created_at: string; updated_at: string
}

export interface Booking {
  id: number; facility: number; facility_name: string; facility_type: FacilityType; facility_capacity: number | null
  booked_by: number; booked_by_name: string; purpose: string; description: string | null
  start_date: string; end_date: string; start_time: string; end_time: string; expected_attendees: number | null; special_requirements: string | null
  status: 'pending' | 'confirmed' | 'declined' | 'cancelled' | 'completed'
  approved_by_name: string; approved_date: string | null; approval_notes: string | null
  cancelled_by_name: string; cancelled_date: string | null; cancellation_reason: string
  notes: string | null; created_at: string
}

export interface MaintenanceRequest {
  id: number; facility: number; facility_name: string; title: string; description: string
  priority: 'low' | 'medium' | 'high' | 'urgent'; status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  requested_by: number; requested_by_name: string; assigned_to: number | null; assigned_to_name: string; assigned_date: string | null
  completed_date: string | null; completion_notes: string | null; estimated_cost: string | null; actual_cost: string | null
  cancelled_by_name: string; cancellation_reason: string; notes: string | null; created_at: string
}

export interface MaintenanceRecord {
  id: number; facility: number; facility_name: string; maintenance_type: 'preventive' | 'corrective' | 'emergency' | 'upgrade' | 'inspection'
  title: string; description: string; performed_by: string | null; scheduled_date: string | null; performed_date: string; next_maintenance_date: string | null
  labor_cost: string; parts_cost: string; total_cost: string; status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  work_performed: string | null; parts_replaced: string | null; recommendations: string | null; notes: string | null; recorded_by_name: string; created_at: string
}

export interface Inspection {
  id: number; facility: number; facility_name: string; inspection_type: string; inspection_date: string; inspector: string | null
  overall_condition: Condition; findings: string | null; recommendations: string | null; action_required: boolean
  follow_up_date: string | null; follow_up_completed: boolean; notes: string | null; recorded_by_name: string; created_at: string
}

export interface FacilityStats {
  total_facilities: number; available_facilities: number; active_bookings: number; pending_bookings: number; todays_bookings: number; my_upcoming_bookings: number
  open_maintenance: number; urgent_maintenance: number; unassigned_maintenance: number; my_open_requests: number
  actions_open: number; follow_ups_overdue: number; unsafe_facilities: number; service_due: number
}

export interface StaffOption { id: number; full_name: string; role: string }

const post = async <T,>(url: string, data: Record<string, unknown> = {}) => (await api.post(url, data)).data as T

export const facilityService = {
  getFacilities: () => getAll<Facility>('/facility/facilities/'),
  createFacility: (data: Record<string, unknown>) => post<Facility>('/facility/facilities/', data),
  updateFacility: async (id: number, data: Record<string, unknown>) => (await api.patch(`/facility/facilities/${id}/`, data)).data as Facility,
  getFacilityStats: async () => (await api.get('/facility/facilities/overview/')).data as FacilityStats,

  getBookings: () => getAll<Booking>('/facility/bookings/'),
  createBooking: (data: Record<string, unknown>) => post<Booking>('/facility/bookings/', data),
  approveBooking: ({ id, note }: { id: number; note?: string }) => post<Booking>(`/facility/bookings/${id}/approve/`, { approval_notes: note ?? '' }),
  declineBooking: ({ id, reason }: { id: number; reason: string }) => post<Booking>(`/facility/bookings/${id}/decline/`, { reason }),
  cancelBooking: ({ id, reason }: { id: number; reason: string }) => post<Booking>(`/facility/bookings/${id}/cancel/`, { reason }),

  getMaintenanceRequests: () => getAll<MaintenanceRequest>('/facility/maintenance-requests/'),
  createMaintenanceRequest: (data: Record<string, unknown>) => post<MaintenanceRequest>('/facility/maintenance-requests/', data),
  startMaintenance: ({ id, ...data }: { id: number; assigned_to?: number | null; estimated_cost?: string }) => post<MaintenanceRequest>(`/facility/maintenance-requests/${id}/start/`, data),
  completeMaintenance: ({ id, ...data }: { id: number; completion_notes: string; actual_cost?: string }) => post<MaintenanceRequest>(`/facility/maintenance-requests/${id}/complete/`, data),
  cancelMaintenance: ({ id, reason }: { id: number; reason: string }) => post<MaintenanceRequest>(`/facility/maintenance-requests/${id}/cancel/`, { reason }),
  getAssignees: async () => (await api.get('/facility/maintenance-requests/assignees/')).data as StaffOption[],

  getRecords: () => getAll<MaintenanceRecord>('/facility/maintenance-records/'),
  createRecord: (data: Record<string, unknown>) => post<MaintenanceRecord>('/facility/maintenance-records/', data),
  updateRecord: async (id: number, data: Record<string, unknown>) => (await api.patch(`/facility/maintenance-records/${id}/`, data)).data as MaintenanceRecord,

  getInspections: () => getAll<Inspection>('/facility/inspections/'),
  createInspection: (data: Record<string, unknown>) => post<Inspection>('/facility/inspections/', data),
  followUpDone: ({ id, notes }: { id: number; notes: string }) => post<Inspection>(`/facility/inspections/${id}/follow-up-done/`, { notes }),
}
